import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  FunnelChart,
  Funnel,
  LabelList,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Printer, Filter } from 'lucide-react';
import { toast } from 'sonner';

const CHART_COLORS = ['#1e3a5f', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#ef4444'];

const ReportsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState([]);
  const [executivePerf, setExecutivePerf] = useState([]);
  const [sources, setSources] = useState([]);
  const [geography, setGeography] = useState({ states: [], cities: [] });
  const [executives, setExecutives] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    executiveId: 'all',
  });

  useEffect(() => {
    fetchReports();
    fetchExecutives();
  }, []);

  const fetchExecutives = async () => {
    try {
      const response = await api.get('/users/executives');
      setExecutives(response.data || []);
    } catch (err) {
      console.error('Failed to load executives');
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.executiveId && filters.executiveId !== 'all') {
        params.append('executiveId', filters.executiveId);
      }

      const [funnelRes, perfRes, sourcesRes, geoRes] = await Promise.all([
        api.get(`/reports/funnel?${params.toString()}`),
        api.get(`/reports/executive-performance?${params.toString()}`),
        api.get('/reports/sources'),
        api.get('/reports/geography'),
      ]);

      setFunnel(funnelRes.data || []);
      setExecutivePerf(perfRes.data || []);
      setSources(sourcesRes.data || []);
      setGeography(geoRes.data || { states: [], cities: [] });
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="reports-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-80" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            Reports & Analytics
          </h1>
          <p className="text-muted-foreground">
            Comprehensive insights into your sales pipeline
          </p>
        </div>
        <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
          <Printer className="mr-2 h-4 w-4" />
          Print Report
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                data-testid="end-date-filter"
              />
            </div>
            <div>
              <Label htmlFor="executive">Executive</Label>
              <Select
                value={filters.executiveId}
                onValueChange={(v) => setFilters({ ...filters, executiveId: v })}
              >
                <SelectTrigger data-testid="executive-filter">
                  <SelectValue placeholder="All Executives" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Executives</SelectItem>
                  {executives.map((exec) => (
                    <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchReports} className="w-full" data-testid="apply-filters-btn">
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="funnel" className="space-y-6">
        <TabsList>
          <TabsTrigger value="funnel" data-testid="funnel-tab">Sales Funnel</TabsTrigger>
          <TabsTrigger value="performance" data-testid="performance-tab">Executive Performance</TabsTrigger>
          <TabsTrigger value="sources" data-testid="sources-tab">Lead Sources</TabsTrigger>
          <TabsTrigger value="geography" data-testid="geography-tab">Geography</TabsTrigger>
        </TabsList>

        <TabsContent value="funnel" className="space-y-6">
          <Card data-testid="funnel-chart">
            <CardHeader>
              <CardTitle>Sales Funnel</CardTitle>
              <CardDescription>Lead progression through stages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnel} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="status" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1e3a5f" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card data-testid="performance-chart">
            <CardHeader>
              <CardTitle>Executive Performance</CardTitle>
              <CardDescription>Comparison of executives by leads and conversions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={executivePerf}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="totalLeads" fill="#1e3a5f" name="Total Leads" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="qualifiedLeads" fill="#3b82f6" name="Qualified" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="outletsOpened" fill="#22c55e" name="Converted" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Performance Table */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Executive</th>
                      <th className="text-right p-2">Total Leads</th>
                      <th className="text-right p-2">Qualified</th>
                      <th className="text-right p-2">Converted</th>
                      <th className="text-right p-2">Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executivePerf.map((exec) => (
                      <tr key={exec.id} className="border-b">
                        <td className="p-2 font-medium">{exec.name}</td>
                        <td className="text-right p-2">{exec.totalLeads}</td>
                        <td className="text-right p-2">{exec.qualifiedLeads}</td>
                        <td className="text-right p-2">{exec.outletsOpened}</td>
                        <td className="text-right p-2">{exec.conversionRate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources" className="space-y-6">
          <Card data-testid="sources-chart">
            <CardHeader>
              <CardTitle>Lead Sources Distribution</CardTitle>
              <CardDescription>Where your leads are coming from</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sources}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="source"
                      label={({ source, percent }) => `${source} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {sources.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="geography" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card data-testid="states-chart">
              <CardHeader>
                <CardTitle>State-wise Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={geography.states.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="state" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1e3a5f" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="cities-chart">
              <CardHeader>
                <CardTitle>Top Cities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={geography.cities.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="city" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
